/**
 * Concrete product configurator — live pricing, validation, cart drawer add.
 * Qty = round(volume / qtyUnit); tenth_m³ variants use qtyUnit 0.1.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import {
  PROP_DELIVERY_FEE,
  PROP_DELIVERY_FEE_TIER1,
  PROP_DELIVERY_FEE_TIER2,
  PROP_DELIVERY_MIN_VOL,
  PROP_FEE_VARIANT_TIER1,
  PROP_FEE_VARIANT_TIER2,
  PROP_POUR_DATE_ISO,
  PROP_VOLUME,
  DEFAULT_FEE_VARIANT_TIER1,
  DEFAULT_FEE_VARIANT_TIER2,
  deliveryFeeDollars,
  deliveryFeeTier,
  feeVariantIdForVolume,
  formatDeliveryFeeProperty,
  formatVolumeProperty,
  pickCanonicalCartProperties,
} from '@theme/concrete-delivery';

const PROP_POUR = '01 · Select weekday or weekend pour';
const PROP_MIX = '02 · Select Mix';
const PROP_DATE = '04 · Date';
const PROP_WINDOW = '05 · Window';

/** @param {string} [raw] */
function parseBlockedDates(raw) {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
  );
}

/** @param {string} isoDate */
function parseLocalDate(isoDate) {
  return new Date(isoDate + 'T12:00:00');
}

/** @param {Date} date */
function isoFromDate(date) {
  return (
    date.getFullYear() +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0')
  );
}

/** @returns {string} */
function todayIso() {
  return isoFromDate(new Date());
}

/** @param {number} [leadDays] */
function earliestBookableIso(leadDays = 2) {
  return isoFromDate(addDays(new Date(), leadDays));
}

/** @param {string} isoDate */
function formatDisplayDate(isoDate) {
  return parseLocalDate(isoDate).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** @param {number} year @param {number} month */
function mondayOffset(year, month) {
  const dow = new Date(year, month, 1, 12, 0, 0).getDay();
  return dow === 0 ? 6 : dow - 1;
}

/** @param {string} value */
function parseTimeToMinutes(value) {
  const match = String(value)
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return 0;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const meridiem = match[3]?.toUpperCase();

  if (meridiem === 'PM' && hours !== 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;

  return hours * 60 + minutes;
}

/** @param {number} totalMinutes */
function formatMinutesToLabel(totalMinutes) {
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const mins = totalMinutes % 60;
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;

  return `${hours12}:${String(mins).padStart(2, '0')} ${meridiem}`;
}

/**
 * @param {string} start
 * @param {string} end
 * @param {number} intervalMins
 * @returns {string[]}
 */
function buildTimeWindows(start, end, intervalMins) {
  const interval = intervalMins > 0 ? intervalMins : 30;
  const startMins = parseTimeToMinutes(start);
  const endMins = parseTimeToMinutes(end);
  if (endMins <= startMins) return [];

  /** @type {string[]} */
  const slots = [];
  for (let cur = startMins; cur + interval <= endMins; cur += interval) {
    slots.push(`${formatMinutesToLabel(cur)} - ${formatMinutesToLabel(cur + interval)}`);
  }
  return slots;
}

/** @param {Date} date @param {number} days */
function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * @param {number} year
 * @param {number} month
 * @param {string} minIso
 * @param {(iso: string) => boolean} isAllowed
 */
function monthHasSelectableDates(year, month, minIso, isAllowed) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = isoFromDate(new Date(year, month, day, 12, 0, 0));
    if (iso >= minIso && isAllowed(iso)) return true;
  }
  return false;
}

/**
 * @param {Date} from
 * @param {string} minIso
 * @param {(iso: string) => boolean} isAllowed
 */
function firstMonthWithSelectableDates(from, minIso, isAllowed) {
  let year = from.getFullYear();
  let month = from.getMonth();

  for (let i = 0; i < 18; i++) {
    if (monthHasSelectableDates(year, month, minIso, isAllowed)) {
      return { year, month };
    }
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }

  return { year: from.getFullYear(), month: from.getMonth() };
}

/** @param {HTMLElement} root */
function initConfigurator(root) {
  if (!root || root.dataset.ccsInit === 'true') return;
  root.dataset.ccsInit = 'true';

  const form = root.querySelector('[data-cp-form]');
  if (!form) return;

  const priceUnit = root.dataset.priceUnit || 'm3';
  const variantMode = root.dataset.variantMode || 'delivery';
  const pourMode = root.dataset.pourMode || '';
  let priceMultiplier = 1;
  if (priceUnit === 'half_m3') priceMultiplier = 2;
  else if (priceUnit === 'tenth_m3') priceMultiplier = 10;

  const qtyUnit = parseFloat(root.dataset.qtyUnit) || (priceUnit === 'tenth_m3' ? 0.1 : priceUnit === 'half_m3' ? 0.5 : 1);
  const minOrderVol = parseFloat(root.dataset.minOrderVolume) || 0.6;

  const config = {
    minVol: parseFloat(root.dataset.minVolume) || 4,
    underFeeTier1: parseFloat(root.dataset.underFeeTier1) || 154,
    underFeeTier2: parseFloat(root.dataset.underFeeTier2) || 121,
    feeVariantTier1: parseInt(root.dataset.feeVariantTier1 || String(DEFAULT_FEE_VARIANT_TIER1), 10) || DEFAULT_FEE_VARIANT_TIER1,
    feeVariantTier2: parseInt(root.dataset.feeVariantTier2 || String(DEFAULT_FEE_VARIANT_TIER2), 10) || DEFAULT_FEE_VARIANT_TIER2,
    satFee: parseFloat(root.dataset.satFee) || 150,
    sunFee: parseFloat(root.dataset.sunFee) || 200,
    moneyFormat: root.dataset.moneyFormat || '${{amount}}',
    weekdayTf: buildTimeWindows(
      root.dataset.weekdayWindowStart || '7:00 AM',
      root.dataset.weekdayWindowEnd || '12:30 PM',
      parseInt(root.dataset.weekdayWindowInterval || '30', 10)
    ),
    saturdayTf: buildTimeWindows(
      root.dataset.saturdayWindowStart || '7:00 AM',
      root.dataset.saturdayWindowEnd || '9:30 AM',
      parseInt(root.dataset.saturdayWindowInterval || '30', 10)
    ),
    sundayTf: (root.dataset.sundayTf || '').split('|').filter(Boolean),
  };

  const weekdayBlocked = parseBlockedDates(root.dataset.weekdayBlockedDates || '');
  const saturdayBlocked = parseBlockedDates(root.dataset.saturdayBlockedDates || '');
  const bookingLeadDays = Math.max(0, parseInt(root.dataset.bookingLeadDays || '2', 10));

  function minBookableIso() {
    return earliestBookableIso(bookingLeadDays);
  }

  const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-id]'));
  const qtyInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-qty]'));
  const pourProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-pour-prop]'));
  const mixProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-mix-prop]'));
  const volumeProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-volume-prop]'));
  const dateProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-date-prop]'));
  const dateIsoProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-date-iso-prop]'));
  const windowProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-window]'));
  const deliveryFeeProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-delivery-fee-prop]'));
  const unitPriceProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-unit-price]'));
  const estTotalProp = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-est-total]'));
  const feeVariantTier1Prop = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-fee-variant-tier1]'));
  const feeVariantTier2Prop = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-fee-variant-tier2]'));
  const variantRadios = form.querySelectorAll('input[name="cp-variant"]');
  const volumeInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-volume]'));
  const dateInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-cp-date]'));
  const timeSelect = /** @type {HTMLSelectElement | null} */ (form.querySelector('[data-cp-time]'));
  const underfeeEl = root.querySelector('[data-cp-underfee]');
  const unitDisplay = root.querySelector('[data-cp-unit-display]');
  const variantTitleEl = root.querySelector('[data-cp-variant-title]');
  const specStrengthEl = root.querySelector('[data-cp-spec-strength]');
  const specSlumpEl = root.querySelector('[data-cp-spec-slump]');
  const specAggregateEl = root.querySelector('[data-cp-spec-aggregate]');
  const specStandardEl = root.querySelector('[data-cp-spec-standard]');

  const submitBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-cp-submit]'));
  const stickyBar = root.querySelector('[data-cp-sticky]');
  const stickyTotal = root.querySelector('[data-cp-sticky-total]');
  const stickySubmit = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-cp-sticky-submit]'));

  const rowBase = root.querySelector('[data-spec-base]');
  const rowDelivery = root.querySelector('[data-spec-delivery]');
  const rowWeekend = root.querySelector('[data-spec-weekend]');
  const rowVolume = root.querySelector('[data-spec-volume]');
  const rowType = root.querySelector('[data-spec-type]');
  const totalEl = root.querySelector('[data-spec-total]');
  const submitLabel = root.querySelector('[data-cp-submit-label]');

  /** @type {(() => void) | null} */
  let renderDatePicker = null;

  /** @param {boolean} hasError */
  function syncDatePickerError(hasError) {
    const picker = root.querySelector('[data-cp-date-picker]');
    if (picker) picker.classList.toggle('cp-date-picker--error', hasError);
  }

  /** @param {number} cents */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return config.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  }

  /** @param {HTMLInputElement | null} radio */
  function updateMixSelectDisplay(radio) {
    const mixSelect = root.querySelector('[data-cp-mix-select]');
    if (!mixSelect || !radio) return;
    const titleEl = mixSelect.querySelector('[data-cp-mix-title]');
    const priceEl = mixSelect.querySelector('[data-cp-mix-price]');
    const priceLabel = radio.dataset.mixPriceLabel || '';
    if (titleEl && radio.dataset.title) titleEl.textContent = radio.dataset.title;
    if (priceEl) {
      if (priceLabel) priceEl.textContent = priceLabel;
      else if (radio.dataset.priceCents) {
        priceEl.textContent = formatMoney(parseInt(radio.dataset.priceCents, 10) * priceMultiplier) + '/m³';
      }
    }
    mixSelect.querySelectorAll('[data-cp-mix-option]').forEach((btn) => {
      const selected = btn.dataset.variantId === radio.value;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  /** @param {HTMLElement | null} mixSelect */
  function closeMixSelect(mixSelect) {
    if (!mixSelect) return;
    mixSelect.classList.remove('is-open');
    const panel = mixSelect.querySelector('[data-cp-mix-panel]');
    const trigger = mixSelect.querySelector('[data-cp-mix-trigger]');
    if (panel instanceof HTMLElement) panel.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  /** @param {HTMLElement | null} mixSelect */
  function openMixSelect(mixSelect) {
    if (!mixSelect) return;
    mixSelect.classList.add('is-open');
    const panel = mixSelect.querySelector('[data-cp-mix-panel]');
    const trigger = mixSelect.querySelector('[data-cp-mix-trigger]');
    if (panel instanceof HTMLElement) panel.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function initMixSelect() {
    const mixSelect = root.querySelector('[data-cp-mix-select]');
    if (!mixSelect) return;
    const trigger = mixSelect.querySelector('[data-cp-mix-trigger]');
    const panel = mixSelect.querySelector('[data-cp-mix-panel]');
    if (!trigger || !panel) return;

    trigger.addEventListener('click', () => {
      if (mixSelect.classList.contains('is-open')) closeMixSelect(mixSelect);
      else openMixSelect(mixSelect);
    });

    mixSelect.querySelectorAll('[data-cp-mix-option]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        const id = btn.dataset.variantId;
        for (const radio of variantRadios) {
          if (radio instanceof HTMLInputElement && radio.value === id && !radio.disabled) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
        closeMixSelect(mixSelect);
      });
    });

    document.addEventListener('click', (e) => {
      if (e.target instanceof Node && !mixSelect.contains(e.target)) closeMixSelect(mixSelect);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeMixSelect(mixSelect);
    });
  }

  /** @param {HTMLInputElement | null} radio */
  function updateIdentityFromVariant(radio) {
    if (!radio) return;
    if (variantTitleEl && variantMode === 'mix' && radio.dataset.title) {
      variantTitleEl.textContent = radio.dataset.title;
    }
    if (specStrengthEl && radio.dataset.specStrength) specStrengthEl.textContent = radio.dataset.specStrength;
    if (specSlumpEl && radio.dataset.specSlump) specSlumpEl.textContent = radio.dataset.specSlump;
    if (specAggregateEl && radio.dataset.specAggregate) specAggregateEl.textContent = radio.dataset.specAggregate;
    if (specStandardEl && radio.dataset.specStandard) specStandardEl.textContent = radio.dataset.specStandard;
  }

  function pourLabelValue() {
    if (pourMode === 'weekday') return root.dataset.weekdayPourLabel || 'Weekday Pour';
    if (pourMode === 'weekend') return root.dataset.weekendPourLabel || 'Weekend Pour';
    return pourProp?.value || '';
  }

  /** @param {HTMLInputElement | null} radio */
  function deliveryKeyFromVariant(radio) {
    if (pourMode === 'weekend') return 'saturday';
    if (pourMode === 'weekday') return 'weekday';
    if (!radio) return 'weekday';
    const t = (radio.dataset.dayKey || radio.dataset.title || '').toLowerCase();
    if (t.indexOf('sun') !== -1) return 'sunday';
    if (t.indexOf('sat') !== -1) return 'saturday';
    return 'weekday';
  }

  function usesPourProductPricing() {
    return pourMode === 'weekday' || pourMode === 'weekend' || variantMode === 'mix';
  }

  function dateModeKey() {
    if (pourMode === 'weekday') return 'weekday';
    if (pourMode === 'weekend') return 'saturday';
    return deliveryKeyFromVariant(selectedVariant());
  }

  /** @param {string} isoDate */
  function dateRejectionReason(isoDate) {
    if (isoDate < minBookableIso()) return 'too_soon';

    const dayOfWeek = parseLocalDate(isoDate).getDay();
    const mode = dateModeKey();

    if (mode === 'weekday') {
      if (dayOfWeek === 0 || dayOfWeek === 6) return 'weekday_only';
      if (weekdayBlocked.has(isoDate)) return 'blocked';
      return null;
    }

    if (mode === 'saturday') {
      if (dayOfWeek !== 6) return 'saturday_only';
      if (saturdayBlocked.has(isoDate)) return 'blocked';
      return null;
    }

    if (mode === 'sunday') {
      if (dayOfWeek !== 0) return 'sunday_only';
      return null;
    }

    return null;
  }

  /** @param {string} isoDate */
  function isDateAllowed(isoDate) {
    return !dateRejectionReason(isoDate);
  }

  /** @param {string} [reason] */
  function dateErrorMessage(reason) {
    if (reason === 'too_soon') {
      return bookingLeadDays === 1
        ? 'Book at least 1 day ahead'
        : `Book at least ${bookingLeadDays} days ahead`;
    }
    if (reason === 'blocked') return 'This date is unavailable';
    if (reason === 'weekday_only') return 'Select a weekday (Mon–Fri)';
    if (reason === 'saturday_only') return 'Select a Saturday';
    if (reason === 'sunday_only') return 'Select a Sunday';
    return 'Select a valid delivery date';
  }

  function enforceDateRules() {
    if (!dateInput?.value) return;
    const reason = dateRejectionReason(dateInput.value);
    if (reason) {
      setFieldError(dateInput, dateErrorMessage(reason));
      dateInput.value = '';
      if (renderDatePicker) renderDatePicker();
    }
  }

  /** @param {number} vol */
  function deliveryFeeTierForVol(vol) {
    return deliveryFeeTier(vol, {
      minVol: config.minVol,
      tier1: config.underFeeTier1,
      tier2: config.underFeeTier2,
    });
  }

  function initVolumeStepper() {
    const stepper = root.querySelector('[data-cp-volume-stepper]');
    const presets = root.querySelector('[data-cp-volume-presets]');
    if (!volumeInput) return;

    const minusBtn = stepper?.querySelector('[data-cp-volume-minus]');
    const plusBtn = stepper?.querySelector('[data-cp-volume-plus]');
    const measureEl = stepper?.querySelector('[data-cp-volume-measure]');
    const step = parseFloat(volumeInput.step) || qtyUnit || 0.1;
    const minVol = parseFloat(volumeInput.min) || minOrderVol;

    function syncVolumeInputWidth() {
      if (!(measureEl instanceof HTMLElement)) return;
      const text = volumeInput.value || formatVolumeValue(minVol);
      measureEl.textContent = text;
      volumeInput.style.width = `${Math.ceil(measureEl.offsetWidth) + 2}px`;
    }

    /** @param {number} vol */
    function snapVolume(vol) {
      const snapped = Math.max(minVol, Math.round(vol / step) * step);
      return Math.round(snapped * 10) / 10;
    }

    /** @param {number} vol */
    function formatVolumeValue(vol) {
      const fixed = snapVolume(vol).toFixed(1);
      return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
    }

    function updatePresetState() {
      if (!presets) return;
      const current = formatVolumeValue(parseFloat(volumeInput.value) || minVol);
      presets.querySelectorAll('[data-cp-volume-preset]').forEach((btn) => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const preset = formatVolumeValue(parseFloat(btn.dataset.cpVolumePreset || '0'));
        btn.classList.toggle('is-active', preset === current);
      });
    }

    function updateStepperButtons() {
      const vol = parseFloat(volumeInput.value) || 0;
      if (minusBtn instanceof HTMLButtonElement) minusBtn.disabled = vol <= minVol + 0.0001;
    }

    /** @param {number} vol */
    function setVolume(vol) {
      volumeInput.value = formatVolumeValue(vol);
      syncVolumeInputWidth();
      updateStepperButtons();
      updatePresetState();
      volumeInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if (minusBtn instanceof HTMLButtonElement) {
      minusBtn.addEventListener('click', () => {
        setVolume((parseFloat(volumeInput.value) || minVol) - step);
      });
    }

    if (plusBtn instanceof HTMLButtonElement) {
      plusBtn.addEventListener('click', () => {
        setVolume((parseFloat(volumeInput.value) || minVol) + step);
      });
    }

    if (presets) {
      presets.querySelectorAll('[data-cp-volume-preset]').forEach((btn) => {
        if (!(btn instanceof HTMLButtonElement)) return;
        btn.addEventListener('click', () => {
          setVolume(parseFloat(btn.dataset.cpVolumePreset || String(minVol)));
        });
      });
    }

    volumeInput.addEventListener('input', () => {
      syncVolumeInputWidth();
      updateStepperButtons();
      updatePresetState();
    });

    volumeInput.addEventListener('blur', () => {
      setVolume(parseFloat(volumeInput.value) || minVol);
    });

    setVolume(parseFloat(volumeInput.value) || minVol);
  }

  function initDatePicker() {
    const picker = root.querySelector('[data-cp-date-picker]');
    const grid = picker?.querySelector('[data-cp-date-grid]');
    const monthLabel = picker?.querySelector('[data-cp-date-month-label]');
    const prevBtn = picker?.querySelector('[data-cp-date-prev]');
    const nextBtn = picker?.querySelector('[data-cp-date-next]');
    const selectedEl = picker?.querySelector('[data-cp-date-selected]');
    const placeholderEl = picker?.querySelector('[data-cp-date-placeholder]');
    const emptyMonthEl = picker?.querySelector('[data-cp-date-empty]');

    if (!(picker instanceof HTMLElement) || !(grid instanceof HTMLElement) || !dateInput) return;

    const minIso = minBookableIso();
    const today = todayIso();
    const now = new Date();
    const initialView = firstMonthWithSelectableDates(now, minIso, isDateAllowed);
    let viewYear = initialView.year;
    let viewMonth = initialView.month;

    /** @param {string} iso */
    function selectDate(iso) {
      if (!isDateAllowed(iso) || iso < minIso) return;
      dateInput.value = iso;
      clearFieldError(dateInput);
      if (renderDatePicker) renderDatePicker();
      recalc();
    }

    function updateSelectedLabel() {
      if (!(selectedEl instanceof HTMLElement) || !(placeholderEl instanceof HTMLElement)) return;
      if (dateInput.value) {
        selectedEl.classList.remove('is-empty');
        placeholderEl.textContent = formatDisplayDate(dateInput.value);
      } else {
        selectedEl.classList.add('is-empty');
        placeholderEl.textContent = 'Select a date';
      }
    }

    renderDatePicker = function render() {
      if (!(monthLabel instanceof HTMLElement)) return;

      monthLabel.textContent = new Date(viewYear, viewMonth, 1).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      });

      const canGoPrev = viewYear > now.getFullYear() || (viewYear === now.getFullYear() && viewMonth > now.getMonth());
      if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = !canGoPrev;

      grid.innerHTML = '';
      const offset = mondayOffset(viewYear, viewMonth);
      const gridStart = new Date(viewYear, viewMonth, 1 - offset, 12, 0, 0);
      let selectableCount = 0;

      for (let i = 0; i < 42; i++) {
        const cell = addDays(gridStart, i);
        const iso = isoFromDate(cell);
        const inMonth = cell.getMonth() === viewMonth;
        const isPast = iso < minIso;
        const reason = inMonth && !isPast ? dateRejectionReason(iso) : null;
        const selectable = inMonth && !isPast && !reason;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cp-date-picker__day';
        btn.textContent = String(cell.getDate());

        if (!inMonth) {
          btn.classList.add('is-outside');
          btn.disabled = true;
          btn.tabIndex = -1;
        } else if (isPast) {
          btn.classList.add('is-past');
          btn.disabled = true;
          btn.tabIndex = -1;
          btn.title =
            iso < today
              ? 'Past date'
              : bookingLeadDays === 1
                ? 'Book at least 1 day ahead'
                : `Book at least ${bookingLeadDays} days ahead`;
        } else if (reason) {
          btn.classList.add('is-unavailable');
          btn.disabled = true;
          btn.tabIndex = -1;
          if (reason === 'blocked') btn.title = 'Unavailable';
          else if (reason === 'weekday_only') btn.title = 'Weekdays only';
          else if (reason === 'saturday_only') btn.title = 'Saturdays only';
          else if (reason === 'sunday_only') btn.title = 'Sundays only';
        } else {
          selectableCount += 1;
          btn.classList.add('is-available');
          btn.addEventListener('click', () => selectDate(iso));
        }

        if (dateInput.value === iso && selectable) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-current', 'date');
        }

        grid.appendChild(btn);
      }

      if (emptyMonthEl instanceof HTMLElement) {
        emptyMonthEl.hidden = selectableCount > 0;
      }

      updateSelectedLabel();
    };

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.addEventListener('click', () => {
        viewMonth -= 1;
        if (viewMonth < 0) {
          viewMonth = 11;
          viewYear -= 1;
        }
        renderDatePicker?.();
      });
    }

    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.addEventListener('click', () => {
        viewMonth += 1;
        if (viewMonth > 11) {
          viewMonth = 0;
          viewYear += 1;
        }
        renderDatePicker?.();
      });
    }

    if (dateInput.value && (!isDateAllowed(dateInput.value) || dateInput.value < minIso)) {
      dateInput.value = '';
    }

    if (dateInput.value) {
      const selected = parseLocalDate(dateInput.value);
      viewYear = selected.getFullYear();
      viewMonth = selected.getMonth();
    } else {
      const nextView = firstMonthWithSelectableDates(now, minIso, isDateAllowed);
      viewYear = nextView.year;
      viewMonth = nextView.month;
    }

    renderDatePicker();
  }

  /** @returns {HTMLInputElement | null} */
  function selectedVariant() {
    for (const radio of variantRadios) {
      if (radio instanceof HTMLInputElement && radio.checked) return radio;
    }
    const first = variantRadios[0];
    return first instanceof HTMLInputElement ? first : null;
  }

  function variantPriceCents() {
    const v = selectedVariant();
    return v ? parseInt(v.dataset.priceCents || '0', 10) || 0 : parseInt(root.dataset.defaultPriceCents || '0', 10) || 0;
  }

  function pricePerM3Cents() {
    return variantPriceCents() * priceMultiplier;
  }

  /** @param {number} vol */
  function cartQuantity(vol) {
    if (vol <= 0) return 1;
    return Math.max(1, Math.round(vol / qtyUnit));
  }

  /** @param {string} key */
  function timeframesForDay(key) {
    if (key === 'saturday') return config.saturdayTf;
    if (key === 'sunday') return config.sundayTf;
    return config.weekdayTf;
  }

  function rebuildTimeframes() {
    if (!timeSelect) return;
    const key = deliveryKeyFromVariant(selectedVariant());
    const list = timeframesForDay(key);
    const prev = timeSelect.value;
    timeSelect.innerHTML = '';
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = list.length ? 'Select window' : 'Select delivery day first';
    timeSelect.appendChild(ph);
    list.forEach((t) => {
      const o = document.createElement('option');
      o.value = t;
      o.textContent = t;
      timeSelect.appendChild(o);
    });
    if (prev && list.indexOf(prev) !== -1) timeSelect.value = prev;
  }

  /** @param {HTMLElement | null} el */
  function clearFieldError(el) {
    if (!el) return;
    el.classList.remove('input-field--error');
    if (el === dateInput) syncDatePickerError(false);
    const field = el.closest('.cproduct__field');
    if (!field) return;
    const err = field.querySelector('.ccs-field-error');
    if (err instanceof HTMLElement) err.hidden = true;
  }

  /** @param {HTMLElement | null} el @param {string} msg */
  function setFieldError(el, msg) {
    if (!el) return;
    el.classList.add('input-field--error');
    if (el === dateInput) syncDatePickerError(true);
    const field = el.closest('.cproduct__field');
    if (!field) return;
    let err = field.querySelector('.ccs-field-error');
    if (!err) {
      err = document.createElement('p');
      err.className = 'ccs-field-error';
      err.setAttribute('role', 'alert');
      field.appendChild(err);
    }
    err.textContent = msg;
    if (err instanceof HTMLElement) err.hidden = false;
  }

  /** @param {boolean} showErrors */
  function validate(showErrors) {
    let valid = true;
    const vol = parseFloat(volumeInput?.value || '') || 0;
    const v = selectedVariant();

    if (!v) valid = false;

    if (vol < minOrderVol) {
      valid = false;
      if (showErrors && volumeInput) {
        setFieldError(volumeInput, 'Minimum order is ' + minOrderVol + ' m³');
      }
    } else if (volumeInput) clearFieldError(volumeInput);

    if (dateInput && !dateInput.value) {
      valid = false;
      if (showErrors) setFieldError(dateInput, 'Select a delivery date');
    } else if (dateInput?.value) {
      const reason = dateRejectionReason(dateInput.value);
      if (reason) {
        valid = false;
        if (showErrors) setFieldError(dateInput, dateErrorMessage(reason));
      } else {
        clearFieldError(dateInput);
      }
    } else if (dateInput) clearFieldError(dateInput);

    if (timeSelect && !timeSelect.value) {
      valid = false;
      if (showErrors) setFieldError(timeSelect, 'Select a time window');
    } else if (timeSelect) clearFieldError(timeSelect);

    return valid;
  }

  function recalc() {
    const vol = parseFloat(volumeInput?.value || '') || 0;
    const perM3Cents = pricePerM3Cents();
    const v = selectedVariant();
    const dayKey = deliveryKeyFromVariant(v);
    const base = vol > 0 && perM3Cents > 0 ? vol * perM3Cents : 0;
    const smallLoadFee = deliveryFeeDollars(vol, {
      minVol: config.minVol,
      tier1: config.underFeeTier1,
      tier2: config.underFeeTier2,
    });
    const smallLoad = smallLoadFee > 0 ? smallLoadFee * 100 : 0;
    const activeFeeTier = deliveryFeeTierForVol(vol);
    let weekend = 0;
    let weekendLabel = '';
    if (!usesPourProductPricing()) {
      if (dayKey === 'saturday') {
        weekend = config.satFee * 100;
        weekendLabel = '+$' + config.satFee.toFixed(0);
      } else if (dayKey === 'sunday') {
        weekend = config.sunFee * 100;
        weekendLabel = '+$' + config.sunFee.toFixed(0);
      }
    }
    const totalCents = base + smallLoad + weekend;
    const qty = cartQuantity(vol);

    if (underfeeEl instanceof HTMLElement) {
      underfeeEl.hidden = vol >= config.minVol;
      underfeeEl.querySelectorAll('[data-cp-underfee-tier]').forEach((tierEl) => {
        if (!(tierEl instanceof HTMLElement)) return;
        const tier = parseInt(tierEl.dataset.cpUnderfeeTier || '0', 10);
        tierEl.classList.toggle('is-active', tier === activeFeeTier);
      });
    }

    if (unitDisplay && perM3Cents > 0) {
      unitDisplay.textContent = formatMoney(perM3Cents);
    }

    updateIdentityFromVariant(v);
    updateMixSelectDisplay(v);

    if (idInput && v) idInput.value = v.value;
    if (qtyInput) qtyInput.value = String(qty);
    const pourVal = pourLabelValue();
    if (pourProp && pourVal) pourProp.value = pourVal;
    if (mixProp && v?.dataset.title) mixProp.value = v.dataset.title;
    if (volumeProp && volumeInput) volumeProp.value = vol > 0 ? formatVolumeProperty(vol) : '';
    if (dateProp && dateInput) {
      dateProp.value = dateInput.value ? formatDisplayDate(dateInput.value) : '';
    }
    if (dateIsoProp && dateInput) dateIsoProp.value = dateInput.value || '';
    if (windowProp && timeSelect) windowProp.value = timeSelect.value;
    if (deliveryFeeProp) {
      deliveryFeeProp.value = formatDeliveryFeeProperty(smallLoadFee, config.moneyFormat);
    }
    if (feeVariantTier1Prop) feeVariantTier1Prop.value = String(config.feeVariantTier1);
    if (feeVariantTier2Prop) feeVariantTier2Prop.value = String(config.feeVariantTier2);
    if (unitPriceProp) unitPriceProp.value = perM3Cents > 0 ? (perM3Cents / 100).toFixed(2) : '';
    if (estTotalProp) estTotalProp.value = totalCents > 0 ? (totalCents / 100).toFixed(2) : '';

    if (rowType) rowType.textContent = root.dataset.productTitle || '—';
    if (rowVolume) rowVolume.textContent = vol > 0 ? vol + ' m³' : '—';
    if (rowBase) {
      rowBase.textContent = vol > 0 && perM3Cents > 0 ? vol + ' × ' + formatMoney(perM3Cents) : '—';
    }
    if (rowDelivery) {
      if (smallLoad > 0) {
        rowDelivery.textContent = '+' + formatMoney(smallLoad);
        rowDelivery.classList.remove('spec-panel__row-value--ok');
      } else if (vol > 0) {
        rowDelivery.textContent = 'Included';
        rowDelivery.classList.add('spec-panel__row-value--ok');
      } else {
        rowDelivery.textContent = '—';
        rowDelivery.classList.remove('spec-panel__row-value--ok');
      }
    }
    if (rowWeekend) {
      const weekendRow = rowWeekend.closest('[data-spec-weekend-row]');
      if (usesPourProductPricing()) {
        if (weekendRow instanceof HTMLElement) weekendRow.hidden = true;
      } else if (weekend > 0) {
        if (weekendRow instanceof HTMLElement) weekendRow.hidden = false;
        rowWeekend.textContent = weekendLabel;
      } else {
        if (weekendRow instanceof HTMLElement) weekendRow.hidden = dayKey === 'weekday';
        rowWeekend.textContent = dayKey === 'weekday' ? 'No surcharge' : '—';
      }
    }

    const totalStr = totalCents > 0 ? formatMoney(totalCents) : '$0.00';
    if (totalEl) totalEl.textContent = totalStr;
    if (stickyTotal) stickyTotal.textContent = totalStr;
    if (submitLabel) {
      submitLabel.textContent = totalCents > 0 ? '▣ REQUEST ORDER — ' + totalStr : '▣ REQUEST ORDER';
    }

    const ok = validate(false) && totalCents > 0;
    if (submitBtn) {
      submitBtn.disabled = !ok;
      submitBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');
    }
    if (stickySubmit) {
      stickySubmit.disabled = !ok;
      stickySubmit.setAttribute('aria-disabled', ok ? 'false' : 'true');
      stickySubmit.textContent = totalCents > 0 ? 'REQUEST ORDER — ' + totalStr : 'REQUEST ORDER';
    }
  }

  function getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }

  function buildCartPayload() {
    /** @type {Record<string, string>} */
    const props = {};
    const pourVal = pourLabelValue();
    const v = selectedVariant();
    const vol = parseFloat(volumeInput?.value || '') || 0;
    if (pourVal) props[PROP_POUR] = pourVal;
    if (v?.dataset.title) props[PROP_MIX] = v.dataset.title;
    if (volumeProp?.value) props[PROP_VOLUME] = volumeProp.value;
    if (dateInput?.value) {
      props[PROP_DATE] = formatDisplayDate(dateInput.value);
      props[PROP_POUR_DATE_ISO] = dateInput.value;
    }
    if (windowProp?.value) props[PROP_WINDOW] = windowProp.value;
    if (deliveryFeeProp?.value) props[PROP_DELIVERY_FEE] = deliveryFeeProp.value;
    props[PROP_DELIVERY_MIN_VOL] = String(config.minVol);
    props[PROP_DELIVERY_FEE_TIER1] = String(config.underFeeTier1);
    props[PROP_DELIVERY_FEE_TIER2] = String(config.underFeeTier2);
    props[PROP_FEE_VARIANT_TIER1] = String(config.feeVariantTier1);
    props[PROP_FEE_VARIANT_TIER2] = String(config.feeVariantTier2);
    if (unitPriceProp?.value) props['_unit_price_per_m3'] = unitPriceProp.value;
    if (estTotalProp?.value) props['_est_total_display'] = estTotalProp.value;

    const sectionIds = getCartSectionIds();
    /** @type {{ items: Array<{ id: number, quantity: number, properties?: Record<string, string> }>, sections?: string }} */
    const payload = {
      items: [{
        id: parseInt(idInput?.value || '0', 10),
        quantity: parseInt(qtyInput?.value || '1', 10) || 1,
        properties: pickCanonicalCartProperties(props),
      }],
    };

    const feeVariantId = feeVariantIdForVolume(vol, props, {
      tier1: config.feeVariantTier1,
      tier2: config.feeVariantTier2,
    });
    if (feeVariantId > 0) {
      payload.items.push({ id: feeVariantId, quantity: 1 });
    }

    if (sectionIds.length) payload.sections = sectionIds.join(',');
    return payload;
  }

  function refreshCart() {
    const cartUrl = window.Theme?.routes?.cart_url
      ? window.Theme.routes.cart_url + '.js'
      : '/cart.js';
    return fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function setSubmitting(disabled) {
    if (submitBtn) submitBtn.disabled = disabled;
    if (stickySubmit) stickySubmit.disabled = disabled;
  }

  function addToCartAjax() {
    if (!validate(true)) return Promise.reject(new Error('Validation failed'));

    setSubmitting(true);

    const payload = buildCartPayload();
    const itemCount = payload.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const variantId = String(payload.items[0]?.id || '');
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: payload.items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    return fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(async (response) => {
        if (response.status) {
          await refreshCart().then((ajaxCart) =>
            deferredEventPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
              detail: {
                didError: true,
                items: ajaxCart.items,
                source: 'concrete-configurator',
                itemCount,
              },
            })
          );
          throw new Error(response.message || 'Add to cart failed');
        }

        const ajaxCart = await refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: false,
            items: ajaxCart.items,
            source: 'concrete-configurator',
            itemCount,
            sections: response.sections,
          },
        });
        recalc();
      })
      .catch((err) => {
        deferredEventPromise.reject(err);
        setSubmitting(false);
        if (err?.message === 'Validation failed') return;
        form.submit();
      });
  }

  variantRadios.forEach((r) => {
    r.addEventListener('change', () => {
      rebuildTimeframes();
      enforceDateRules();
      if (renderDatePicker) renderDatePicker();
      recalc();
    });
  });

  [volumeInput, timeSelect].forEach((el) => {
    if (!el) return;
    el.addEventListener('input', recalc);
    el.addEventListener('change', recalc);
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!validate(true)) return;
    addToCartAjax();
  });

  if (stickyBar && stickySubmit) {
    stickySubmit.addEventListener('click', () => {
      if (!submitBtn || submitBtn.disabled) return;
      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true }));
    });
    if ('IntersectionObserver' in window) {
      const target = root.querySelector('[data-cp-spec-panel]');
      if (target) {
        const obs = new IntersectionObserver(
          (entries) => {
            if (stickyBar instanceof HTMLElement) {
              stickyBar.hidden = entries[0].isIntersecting;
            }
          },
          { threshold: 0, rootMargin: '0px 0px -80px 0px' }
        );
        obs.observe(target);
      }
    }
  }

  initMixSelect();
  initVolumeStepper();
  initDatePicker();
  rebuildTimeframes();
  recalc();
}

function boot() {
  document.querySelectorAll('[data-concrete-configurator]').forEach((root) => {
    if (root instanceof HTMLElement) initConfigurator(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', boot);
